import { getOrganizationSubscription } from '../services/subscriptionService';

const sleep = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

let activePlanUpgradeWait = null;

export function getSubscriptionPlanCode(subscription) {
  if (!subscription) return null;
  const code =
    subscription.planCode
    ?? subscription.plan?.code
    ?? subscription.raw?.plan?.code;
  return code ? String(code).toLowerCase() : null;
}

export function formatAmountCents(amountCents, currency = 'usd') {
  if (amountCents == null || Number.isNaN(Number(amountCents))) {
    return null;
  }
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: String(currency).toUpperCase(),
    }).format(Number(amountCents) / 100);
  } catch {
    return `$${(Number(amountCents) / 100).toFixed(2)}`;
  }
}

export function getBillingErrorMessage(err, fallback = 'Something went wrong.') {
  const data = err?.response?.data;
  if (data?.message) return data.message;
  if (err?.message) return err.message;
  return fallback;
}

export function getBillingErrorCode(err) {
  return err?.response?.data?.code ?? null;
}

/**
 * Normalize subscribe / create-send-payment `data` payload.
 */
export function normalizePaymentFlowData(data) {
  if (!data || typeof data !== 'object') {
    return {
      paymentFlow: null,
      requiresPaymentUi: false,
      paymentCompleted: false,
      clientSecret: null,
      paymentIntentId: null,
      amountCents: null,
      currency: 'usd',
      planCode: null,
      status: null,
    };
  }

  return {
    paymentFlow: data.payment_flow ?? data.paymentFlow ?? null,
    requiresPaymentUi: Boolean(
      data.requires_payment_ui ?? data.requiresPaymentUi
    ),
    paymentCompleted: Boolean(
      data.payment_completed ?? data.paymentCompleted
    ),
    planSynced: data.plan_synced === true || data.planSynced === true,
    clientSecret: data.client_secret ?? data.clientSecret ?? null,
    paymentIntentId: data.payment_intent_id ?? data.paymentIntentId ?? null,
    amountCents: data.amount_cents ?? data.amountCents ?? null,
    currency: (data.currency || 'usd').toLowerCase(),
    planCode: data.plan_code ?? data.planCode ?? null,
    packCode: data.pack_code ?? data.packCode ?? null,
    envelopes: data.envelopes ?? null,
    envelopeCredits:
      data.envelope_credits
      ?? data.envelopeCredits
      ?? null,
    status: data.status ?? null,
    alreadyPaid: Boolean(data.already_paid ?? data.alreadyPaid),
  };
}

/**
 * Poll until org subscription reflects expected plan (webhook delay).
 * Dedupes concurrent callers so checkout + billing do not each run 10+ requests.
 *
 * @param {Function} [fetchSubscription] async () => subscription — e.g. Redux thunk unwrap
 * @returns {Promise<object|null>}
 */
export async function waitForPlanUpgrade(
  expectedPlanCode,
  {
    maxAttempts = 5,
    delayMs = 2000,
    fetchSubscription = getOrganizationSubscription,
  } = {}
) {
  const expected = String(expectedPlanCode || '').trim().toLowerCase();
  if (!expected) return null;

  if (activePlanUpgradeWait) {
    return activePlanUpgradeWait;
  }

  activePlanUpgradeWait = (async () => {
    let lastSubscription = null;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      lastSubscription = await fetchSubscription();
      if (getSubscriptionPlanCode(lastSubscription) === expected) {
        return lastSubscription;
      }
      if (attempt < maxAttempts - 1) {
        await sleep(delayMs);
      }
    }

    return lastSubscription;
  })();

  try {
    return await activePlanUpgradeWait;
  } finally {
    activePlanUpgradeWait = null;
  }
}
